/**
 * Vanilla JS post archive – sorting, pagination, URL management
 * Works with pure-PHP rendered grid in content.php
 */

export default function initPostArchiveVanilla() {
  const root = document.getElementById("post-archive");
  if (!root) return;

  const grid = root.querySelector("#posts-grid");
  const paginationNav = root.querySelector("#post-pagination");
  const sortLabel = root.querySelector("#post-sort-label");
  const emptyState = root.querySelector("#post-empty-state");

  const postsPerPage = parseInt(root.dataset.postsPerPage, 10) || 10;

  // ── state ────────────────────────────────────────────────────────
  let currentSort = "date-desc"; // default matches server order
  let currentPage = 1;

  // All post cards
  const allCards = () => Array.from(grid.querySelectorAll(".post-card-col"));

  // ── URL helpers (match React behaviour) ──────────────────────────
  function getPageFromUrl() {
    try {
      const url = new URL(window.location.href);
      const paged =
        url.searchParams.get("paged") || url.searchParams.get("page");
      if (paged) {
        const n = parseInt(paged, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
      const match = url.pathname.match(/\/page\/(\d+)\/?$/);
      if (match && match[1]) {
        const n = parseInt(match[1], 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
    } catch (_e) {
      /* noop */
    }
    return 1;
  }

  function buildUrlForPage(page) {
    const url = new URL(window.location.href);
    let path = url.pathname.replace(/\/page\/\d+\/?$/, "/");
    if (!path.endsWith("/")) path += "/";
    url.pathname = page > 1 ? `${path}page/${page}/` : path;
    url.searchParams.delete("paged");
    url.searchParams.delete("page");
    return url.toString();
  }

  // ── sorting ──────────────────────────────────────────────────────
  function applySort() {
    const cards = allCards();
    cards.sort((a, b) => {
      switch (currentSort) {
        case "date-asc":
          return (
            parseInt(a.dataset.date, 10) - parseInt(b.dataset.date, 10)
          );
        case "title-asc":
          return (a.dataset.title || "").localeCompare(
            b.dataset.title || "",
            "pl"
          );
        case "title-desc":
          return (b.dataset.title || "").localeCompare(
            a.dataset.title || "",
            "pl"
          );
        default: // date-desc
          return (
            parseInt(b.dataset.date, 10) - parseInt(a.dataset.date, 10)
          );
      }
    });
    cards.forEach((c) => grid.appendChild(c));
  }

  // ── pagination ───────────────────────────────────────────────────
  function getTotalPages() {
    return Math.max(1, Math.ceil(allCards().length / postsPerPage));
  }

  function clamp(page) {
    return Math.min(Math.max(1, page), getTotalPages());
  }

  function applyPagination() {
    const cards = allCards();
    const start = (currentPage - 1) * postsPerPage;
    const end = start + postsPerPage;

    cards.forEach((col, i) => {
      col.style.display = i >= start && i < end ? "" : "none";
    });

    if (cards.length === 0 && emptyState) {
      emptyState.style.display = "";
      grid.style.display = "none";
    } else if (emptyState) {
      emptyState.style.display = "none";
      grid.style.display = "";
    }

    renderPagination();
  }

  function goToPage(page, pushState = true) {
    currentPage = clamp(page);
    applyPagination();
    if (pushState) {
      const nextUrl = buildUrlForPage(currentPage);
      window.history.pushState({ paged: currentPage }, "", nextUrl);
    }
    grid.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ── render pagination UI ─────────────────────────────────────────
  function renderPagination() {
    if (!paginationNav) return;
    const totalPages = getTotalPages();
    const ul = paginationNav.querySelector(".pagination");
    ul.innerHTML = "";

    if (totalPages <= 1) {
      paginationNav.style.display = "none";
      return;
    }
    paginationNav.style.display = "";

    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let startPage = Math.max(1, currentPage - half);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous
    ul.appendChild(
      makePageItem("&laquo;", currentPage - 1, currentPage === 1)
    );

    // First page + ellipsis
    if (startPage > 1) {
      ul.appendChild(makePageItem("1", 1));
      if (startPage > 2)
        ul.appendChild(makePageItem("...", null, true));
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      ul.appendChild(makePageItem(String(i), i, false, i === currentPage));
    }

    // Last page + ellipsis
    if (endPage < totalPages) {
      if (endPage < totalPages - 1)
        ul.appendChild(makePageItem("...", null, true));
      ul.appendChild(makePageItem(String(totalPages), totalPages));
    }

    // Next
    ul.appendChild(
      makePageItem("&raquo;", currentPage + 1, currentPage === totalPages)
    );
  }

  function makePageItem(label, page, disabled = false, active = false) {
    const li = document.createElement("li");
    li.className = `page-item${disabled ? " disabled" : ""}${active ? " active" : ""}`;

    if (disabled || active || page === null) {
      const span = document.createElement("span");
      span.className = "page-link";
      span.innerHTML = label;
      if (active) span.setAttribute("aria-current", "page");
      if (disabled) span.setAttribute("aria-disabled", "true");
      li.appendChild(span);
    } else {
      const a = document.createElement("a");
      a.className = "page-link";
      a.href = buildUrlForPage(page);
      a.innerHTML = label;
      a.addEventListener("click", (e) => {
        e.preventDefault();
        goToPage(page);
      });
      li.appendChild(a);
    }
    return li;
  }

  // ── event listeners ──────────────────────────────────────────────
  // Sort buttons
  root.querySelectorAll(".post-sort-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      currentSort = btn.dataset.sort;
      if (sortLabel) sortLabel.textContent = btn.textContent;
      currentPage = 1;
      applySort();
      applyPagination();
      // Update URL to page 1
      window.history.replaceState(
        { paged: 1 },
        "",
        buildUrlForPage(1)
      );
    });
  });

  // Back/forward navigation
  window.addEventListener("popstate", () => {
    currentPage = clamp(getPageFromUrl());
    applyPagination();
  });

  // Mobile filter hamburger (categories are server-side links, but keep the mobile overlay toggle)
  const hamburger = root.querySelector("#post-filter-hamburger");
  const mobileMenu = root.querySelector("#post-filter-mobile");
  const mobileClose = root.querySelector("#post-filter-mobile-close");

  if (hamburger && mobileMenu) {
    hamburger.addEventListener("click", () => {
      mobileMenu.classList.add("active");
      document.body.style.overflow = "hidden";
    });
  }
  if (mobileClose && mobileMenu) {
    mobileClose.addEventListener("click", () => {
      mobileMenu.classList.remove("active");
      document.body.style.overflow = "";
    });
  }

  // ── init ─────────────────────────────────────────────────────────
  currentPage = clamp(getPageFromUrl());
  applyPagination();
}
